/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Bot, Save } from 'lucide-react';
import { aiAgentService } from '../services/aiAgentService';
import {
  knowledgeBaseService, AI_AGENT_ERROR_CODES, getApiErrorMessage
} from '../services/knowledgeBaseService';
import AiAgentFormSections, {
  parseMcpServers, validateAiAgentForm
} from '../components/aiAgents/AiAgentFormSections';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/ai-agents';

const EditAiAgent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: agentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Platform-wide view carries the account in the URL; account / sub-account users use their own.
  const showAccountField = !user?.accountUID;
  const accountId = showAccountField ? (searchParams.get('accountId') || '') : user?.accountUID;
  const accountQuery = showAccountField && accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
  const accountParams = showAccountField ? { accountId } : {};

  const [form, setForm] = useState(null);
  // The agent's original type/provider/model, offered as "(current)" when not in the catalogue.
  const [initial, setInitial] = useState({ type: '', provider: '', model: '' });
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [droppedKbCount, setDroppedKbCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  // Set when the Agentic AI platform itself does not respond properly.
  const [serviceError, setServiceError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const detailRoute = `${LIST_ROUTE}/${encodeURIComponent(agentId)}${accountQuery}`;
  const backToDetail = () => navigate(detailRoute);

  useEffect(() => {
    if (user && showAccountField && !accountId) {
      navigate(LIST_ROUTE, { replace: true });
    }
  }, [user, showAccountField, accountId, navigate]);

  // Load the agent and the account's knowledge bases (the only ones that can be linked).
  useEffect(() => {
    if (!accountId) { return undefined; }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        setIntegrationError(null);
        setServiceError(null);
        const [agentRes, kbRes] = await Promise.all([
          aiAgentService.getAiAgent(agentId, accountParams),
          knowledgeBaseService.getKnowledgeBases(accountParams),
        ]);
        if (cancelled) { return; }
        const agent = agentRes.data?.data;
        const kbs = Array.isArray(kbRes.data?.data) ? kbRes.data.data : [];
        const kbIds = new Set(kbs.map((kb) => String(kb.id)));
        const linked = (agent?.knowledgeBases || []).map((kb) => kb.id);
        const kept = linked.filter((id) => kbIds.has(id));

        setKnowledgeBases(kbs);
        setDroppedKbCount(linked.length - kept.length);
        setInitial({ type: agent?.type || '', provider: agent?.provider || '', model: agent?.model || '' });
        setForm({
          name: agent?.name || '',
          role: agent?.role || '',
          type: agent?.type || '',
          provider: agent?.provider || '',
          model: agent?.model || '',
          temperature: agent?.temperature ?? '',
          temperatureDirty: false,
          greetingMessage: agent?.greetingMessage || '',
          prompt: agent?.prompt || '',
          knowledgeBaseIds: kept,
          // Raw MCP config (returned only to users who can update agents), pretty-printed.
          mcpServersText: agent?.mcpServersConfig?.length
            ? JSON.stringify(agent.mcpServersConfig, null, 2)
            : '',
          mcpServersDirty: false,
          isLive: Boolean(agent?.isLive),
        });
      } catch (err) {
        if (cancelled) { return; }
        const message = getApiErrorMessage(err, 'Failed to load AI agent');
        if (getAgenticAiServiceError(err)) {
          setServiceError(getAgenticAiServiceError(err));
        } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
          setIntegrationError(message);
        } else if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          toastService.error(message);
        }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, accountId, reloadKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateAiAgentForm(form, { validateMcp: true });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) { return; }
    // Emptying the prefilled box removes all servers.
    const mcpServers = form.mcpServersDirty ? (parseMcpServers(form.mcpServersText).servers ?? []) : null;
    try {
      setSubmitting(true);
      await aiAgentService.updateAiAgent(agentId, {
        name: form.name.trim(),
        role: form.role.trim(),
        type: form.type,
        provider: form.provider,
        model: form.model,
        // Only sent when changed, so an off-grid value (e.g. 0.25) is kept as-is.
        ...(form.temperatureDirty && form.temperature !== '' && { temperature: Number(form.temperature) }),
        greetingMessage: form.greetingMessage.trim(),
        prompt: form.prompt.trim(),
        knowledgeBaseIds: form.knowledgeBaseIds,
        isLive: form.isLive,
        // Only sent when edited, so untouched MCP config is kept exactly as it is upstream.
        ...(mcpServers && { mcpServers }),
      }, accountParams);
      toastService.success('AI agent updated successfully');
      backToDetail();
    } catch (err) {
      toastService.error(getApiErrorMessage(err, 'Failed to update AI agent'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-base-200/50 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      );
    }
    if (serviceError) {
      return <AgenticAiUnavailable error={serviceError} onRetry={() => setReloadKey((k) => k + 1)} />;
    }
    if (integrationError) {
      return (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      );
    }
    if (notFound || !form) {
      return (
        <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
            <Bot size={28} />
          </div>
          <div>
            <h3 className="text-base font-black text-base-content">AI agent not found</h3>
            <p className="text-xs text-base-content/40 max-w-sm mt-1">
              It may have been removed, or it does not belong to this {ACCOUNT_LABEL.singularLower}.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`${LIST_ROUTE}${accountQuery}`)}
            className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
          >
            Back to AI Agents
          </button>
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <AiAgentFormSections
          form={form}
          setForm={setForm}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
          knowledgeBases={knowledgeBases}
          initial={initial}
          droppedKbCount={droppedKbCount}
          showLive
          showMcpServers
        />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={backToDetail}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="w-full sm:flex-1 btn-premium justify-center">
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={backToDetail}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Agent Details
        </button>
      </div>

      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="space-y-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
            Edit AI Agent
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight break-words">
            {form?.name || location.state?.agent?.name || 'AI Agent'}
          </h1>
          <p className="text-xs text-base-content/50">
            Update the agent's configuration, prompt and linked knowledge bases.
          </p>
        </div>
      </div>

      {renderContent()}
    </div>
  );
};

export default EditAiAgent;
